import * as React from "react";
import {
    Box,
    Container,
    Typography,
    Stack,
    Divider,
    Chip,
    Paper,
} from "@mui/material";

import javaHero from "../assets/images/java-renaissance.jpg";
import loomImg from "../assets/images/java-loom.jpg";
import panamaImg from "../assets/images/java-panama.jpg";
import valhallaImg from "../assets/images/java-valhalla.jpg";
import cracImg from "../assets/images/java-crac.jpg";
import amberImg from "../assets/images/java-amber.jpg";
import rewriteImg from "../assets/images/java-rewrite.jpg";
import {Helmet} from "react-helmet-async";

const fontFamily =
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const TITLE = "The Java Renaissance: Why the “Rewrite in Rust” Era Is Quietly Ending";

const AUTHOR = "Aggrey Lelei";
const SUB = "Distributed Systems · JVM · Infrastructure · Cloud Computing";
const META = "6 min read · Jan 5, 2026";

const TAGS = [
    "Java",
    "JVM",
    "Project Loom",
    "Project Panama",
    "Valhalla",
    "CRaC",
    "Project Amber",
];

const sections: Array<{
    title: string;
    image?: string;
    imageAlt?: string;
    paragraphs: React.ReactNode[];
}> = [
    {
        title: "Java has been the elephant in the room for a while",
        image: rewriteImg,
        imageAlt: "Historic rewrite era",
        paragraphs: [
            <>
                For much of the last decade, rewriting Java systems became a rite of passage for serious infrastructure teams.
                If you wanted to scale cheaply, survive unpredictable traffic, or keep cloud bills under control, the accepted
                wisdom was blunt: Java had hit its limits. This belief wasn’t ideological. Teams watched thread
                counts climb into the tens of thousands, garbage collection pauses bleed into tail latencies, and memory limits
                get violated in containers that should have been fine. Systems stayed correct but became operationally fragile.
                <br/>
                <i>Java failed expensively. </i>
                In the pre-cloud era, many of these issues were survivable. Services ran for months. Heaps grew large and
                stayed warm. Startup cost was amortized across long-lived processes. A few extra gigabytes of RAM could hide
                architectural inefficiencies.
                <br/><i>Cloud-native architectures removed that buffer.</i> <br/>
                <ul>
                    <li>Autoscaling punished slow startup.</li>
                    <li>Multitenancy exposed memory overhead.</li>
                    <li>High fan-out microservices amplified synchronization costs.</li>
                </ul>
                Traffic spikes turned theoretical limits into incident tickets. What once felt like edge cases became routine.
                <br/>
                So teams rewrote:
                <ul>
                    <li>DoorDash moved critical services to Go.</li>
                    <li>Discord rebuilt infrastructure in Rust.</li>
                    <li>Cloudflare invested heavily outside the JVM.</li>
                </ul>
                These weren’t résumé-driven rewrites. They were responses to concrete,
                painful constraints: heavyweight threads, object-dense heaps, unreliable native boundaries, and cold starts
                incompatible with elastic systems. What’s changed isn’t Java’s reputation, that skepticism still lingers.
                What’s changed is Java’s internal architecture. Over the last several years, OpenJDK stopped polishing
                legacy assumptions and started removing them. One by one, the structural reasons behind those rewrites
                are being dismantled.
        </>
            ],
    },
    {
        title: "Concurrency was the first fault line",
        image: loomImg,
        imageAlt: "Virtual threads and concurrency",
        paragraphs: [
            <>
                Classic Java servers mapped each incoming request to an operating system thread.
                For years, this model scaled “well enough” by adding more machines and increasing
                thread pool sizes. Eventually, that strategy hit a wall and stopped working.
               <br/>
                    Operating system threads are heavyweight. Each thread reserves stack memory whether
                    it uses it or not, context switches are expensive, and kernel schedulers are designed
                    for thousands of threads not hundreds of thousands. As concurrency increases,
                    especially in I/O-bound workloads, overhead starts to dominate useful work.
               <br/>
                <p>
                    To cope, Java frameworks began contorting themselves. Async callbacks proliferated.
                    Reactive streams became fashionable. Thread pools were tuned by trial, error, and
                    superstition. Debugging grew painful as stack traces stopped resembling real control
                    flow.
                </p>

                <p>
                    Go’s goroutines reset expectations by introducing a different implementation.
                    Running millions of concurrent tasks became normal. Blocking I/O was
                    no longer taboo. In comparison, the JVM’s thread-per-request model suddenly looked
                    obsolete.
                </p>

                <p>
                    Project Loom doesn’t try to copy goroutines. Instead, it removes the constraint that
                    made them necessary. Virtual threads are cheap and abundant,<i> and they’re scheduled by
                    the JVM rather than the operating system. </i> When a virtual thread blocks on I/O, it is
                    transparently unmounted from its carrier thread, freeing the OS thread to run
                    something else. No callbacks. No reactive rewrites. No ecosystem fracture.
                </p>

                <p>
                    The deeper victory is continuity. Existing blocking APIs, JDBC drivers, and
                    synchronous libraries scale without modification. Debugging stays familiar. Stack
                    traces remain meaningful. Observability tooling continues to work. This is
                    better and scalable concurrency without abandoning Java’s mental model.
                </p>
            </>

        ]
    },
    {
        title: "Native code exposed the JVM’s sharpest edges",
        image: panamaImg,
        imageAlt: "Native interoperability",
        paragraphs: [
            <>
                Normally JNI worked until it didn’t. At small scale, native integrations were tolerable but
                became liabilities at large scale. JNI exposed raw pointers, relied on manual
                memory discipline and trusted developers not to break invariants the JVM itself
                enforces everywhere else.
                <br/>
                Native code crashed instead of degrading degrade gracefully. For teams
                operating latency-sensitive systems or interacting with hardware, this fragility
                was unacceptable. Memory safety ended exactly where performance began.
                <br/>
                Rust’s rise here wasn’t accidental. Its guarantees around memory layout, lifetimes,
                and ownership gave teams confidence that native code wouldn’t take the process down
                with it.
                <br/>
                Project Panama fundamentally changes this relationship. Instead of treating native
                code as an opaque danger zone, the JVM now models memory segments, lifetimes, and
                calling conventions explicitly. Java code can interact with native libraries
                through structured APIs that make ownership and scope clear.
                <br/>
                The result is native interoperability that is fast, predictable, and survivable.
                Java no longer has to choose between performance and safety. Native code becomes an
                extension of the JVM’s model rather than a breach in it.

            </>
        ],
    },
    {
        title: "Memory density was the problem no profiler screamed about",
        image: valhallaImg,
        imageAlt: "Value types and compact data",
        paragraphs: [
            <>
                Many of Java’s most expensive failures were memory-bound.
                Object-oriented design came with hidden costs such as headers, pointers, indirections,
                and fragmented layouts. Millions of small objects scattered across the heap
                translated into cache misses, bloated memory footprints and garbage collectors
                spending more time moving metadata than data.
               <br/>
                This manifests subtly but brutally at scale. Memory bandwidth saturates. GC
                cycles lengthen. Tail latencies climb even when CPU usage looks healthy.
                <br/><i>Discord’s rewrite was driven by data density.</i><br/>
                Flattened structures and contiguous memory unlocked dramatic improvements in
                cache locality and memory efficiency.
               <br/>
                Project Valhalla brings that capability into the JVM itself. Value classes remove
                object headers and pointer indirection, allowing data to be stored contiguously
                while preserving type safety and expressiveness. Code remains readable. Data
                becomes compact.
               <br/>
                Project Lilliput attacks the remaining overhead by shrinking object headers where
                objects are still required. Together, these changes strike at one of the JVM’s
                oldest weaknesses. In high-scale systems, better memory density not only
                improves performance,<i> it directly reduces infrastructure cost and operational risk.</i>
            </>
        ],
    },
    {
        title: "Cold starts collided with cloud economics",
        image: cracImg,
        imageAlt: "Snapshot and warm-start techniques",
        paragraphs: [
            <>
                Java assumed <b>longevity</b>. The cloud assumes <b>ephemerality</b>. Containers spin up and down
                constantly while serverless platforms penalize idle memory. Autoscalers expect instances
                to become useful almost immediately. In this world, multi-second startup times
                are minor annoyances and more architectural failures.
                <br/>
                Native images helped but at a cost. Reflection broke, dynamic loading disappeared,
                observability suffered. Many teams found themselves trading runtime flexibility for
                startup speed and not always winning.
                <br/>
                <b>Project CRaC</b> takes a different approach. Instead of reinitializing the JVM repeatedly,
                CRaC snapshots a fully initialized, fully warmed process and restores it almost
                instantly. Initialization cost is paid once and reused.
                <br/>
                <b>Project Leyden</b> pushes the idea further by shifting work to build time. Runtime
                initialization paths shrink. The JVM starts closer to a steady state rather than a
                cold one. Together, these projects realign Java with cloud economics. Startup time
                stops being a deal-breaker and becomes an engineering choice.
            </>
        ],
    },
    {
        title: "Ergonomics mattered more than we admitted",
        image: amberImg,
        imageAlt: "Language ergonomics and modern Java",
        paragraphs: [
            <>
                Hardware has shown that performance alone doesn’t win ecosystems. Developers choose tools that reduce
                cognitive load. It reached a time we realised java’s verbosity wasn’t just aesthetic. The time needed
                to code down an intent was obscured by boilerplate which slowed reasoning. This friction accumulated
                as systems grew larger and teams moved faster. In that environment, Kotlin’s rise felt less like
                rebellion and more like relief.
                <br/>
                <i>Project Amber modernized Java without fracturing it.</i>
                Records removed boilerplate while preserving clarity. Pattern matching reduced
                unsafe casts. Sealed types made invariants explicit. Switch expressions became
                expressive rather than error-prone.
                <br/>
                Modern Java is leaner, clearer, and closer to how engineers reason today.
                The language stopped defending its past and started serving its users.
                <br/>
            </>
        ],
    }
];

function StageInlineImage({ src, alt }: { src: string; alt: string }) {
    return (
        <Paper
            elevation={0}
            sx={{
                float: { xs: "none", sm: "left" },
                width: { xs: "100%", sm: "30%" },
                maxWidth: { sm: 180 },
                mr: { xs: 0, sm: 2 },
                mb: { xs: 1.5, sm: 1 },
                borderRadius: 2.5,
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
            }}
        >
            <Box
                component="img"
                src={src}
                alt={alt}
                //loading="lazy"
                //width="1600"
                //height="900"
                sx={{ width: "100%", height: "auto", display: "block", objectFit: "cover" }}
            />
        </Paper>
    );
}

export default function JavaRenaissance() {
    React.useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Helmet>
                <title>The Java Renaissance : Why the “Rewrite in Rust” Era Is Ending</title>

                <meta
                    name="description"
                    content="Explore why Java is resurging with Project Loom, Panama, Valhalla, and CRaC, and why the 'rewrite in Rust' trend is slowing in modern distributed systems."
                />

                <link
                    rel="canonical"
                    href="https://aggreylelei.com/blog/java-renaissance/"
                />

                <meta property="og:type" content="article" />
                <meta
                    property="og:title"
                    content="The Java Renaissance : Why the “Rewrite in Rust” Era Is Ending"
                />
                <meta
                    property="og:description"
                    content="Explore why Java is resurging with Project Loom, Panama, Valhalla, and CRaC, and why the 'rewrite in Rust' trend is slowing in modern distributed systems."
                />
                <meta
                    property="og:url"
                    content="https://aggreylelei.com/blog/java-renaissance"
                />
                <meta
                    property="og:image"
                    content="https://aggreylelei.com/og-java.jpg"
                />

                <meta property="article:published_time" content="2026-01-5T09:00:00+03:00" />
                <meta property="article:modified_time" content="2026-03-31T09:00:00+03:00" />
                <meta property="article:author" content="Aggrey Lelei" />
                <meta property="og:site_name" content="Aggrey Lelei" />

                <meta name="author" content="Aggrey Lelei" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="The Java Renaissance : Why the “Rewrite in Rust” Era Is Ending"
                />
                <meta
                    name="twitter:description"
                    content="Explore why Java is resurging with Project Loom, Panama, Valhalla, and CRaC, and why the 'rewrite in Rust' trend is slowing in modern distributed systems."
                />
                <meta
                    name="twitter:image"
                    content="https://aggreylelei.com/og-java.jpg"
                />
            </Helmet>
            <Box
                component="main"
                sx={{ fontFamily, bgcolor: "background.default", color: "text.primary", py: { xs: 5, md: 8 } }}
            >
                <Container maxWidth="md">
                    <Stack spacing={2.25} sx={{ mb: 4 }}>
                        <Paper elevation={0} sx={{ borderRadius: 3, overflow: "hidden", border: 1, borderColor: "divider", mb: 2 }}>
                            <Box sx={{ bgcolor: "grey.100", aspectRatio: "16 / 4" }}>
                                <Box
                                    component="img"
                                    src={javaHero}
                                    alt="Article hero"
                                    // loading="lazy"
                                    // width="1600"
                                    // height="900"
                                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                />
                            </Box>
                        </Paper>

                        <Typography variant="h3" component="h1" sx={{ fontFamily, fontWeight: 900, letterSpacing: -0.8, lineHeight: 1.12, fontSize: { xs: "1.3rem", sm: "1.9rem", md: "2.3rem" } }}>
                            {TITLE}
                        </Typography>

                        <Typography variant="subtitle1" component="p" sx={{ fontFamily, fontWeight: 600, color: "text.secondary" }}>
                            {AUTHOR}
                        </Typography>

                        <Typography variant="body2" component="p" sx={{ fontFamily, fontWeight: 400, color: "text.secondary", lineHeight: 1.55 }}>
                            {SUB}
                        </Typography>

                        <Typography variant="body2" sx={{ fontFamily, color: "text.secondary" }}>
                            {META}
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ pt: 0.5 }}>
                            {TAGS.map((tag) => (
                                <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontFamily, borderRadius: 999 }} />
                            ))}
                        </Stack>

                        <Divider sx={{ mt: 1 }} />
                    </Stack>

                    <Stack spacing={3}>
                        <Typography component="p" sx={{ fontFamily, fontSize: "1.05rem", lineHeight: 1.85 }}>
                            Obviously Java had a scaling problem and people felt it in their pager rotations. But the platform
                            that spawned the internet-scale era didn't stay still. The pieces that made teams abandon the JVM
                            are now being addressed at the platform level.
                        </Typography>

                        {sections.map((section, idx) => (
                            <Box key={section.title} sx={{ mt: idx === 0 ? 0 : 1 }}>
                                <Typography variant="h5" component="h2" sx={{ fontFamily, fontWeight: 850, lineHeight: 1.25, letterSpacing: -0.2, mb: 1.25 }}>
                                    {section.title}
                                </Typography>

                                <Box sx={{ overflow: "hidden" }}>
                                    {section.image && section.imageAlt ? <StageInlineImage src={section.image} alt={section.imageAlt} /> : null}

                                    {section.paragraphs.map((p, pIdx) => (
                                        <Typography key={`${section.title}-${pIdx}`} component="p" sx={{ fontFamily, fontSize: "1.02rem", lineHeight: 1.85, color: "text.primary", mb: 1.25 }}>
                                            {p}
                                        </Typography>
                                    ))}
                                </Box>
                            </Box>
                        ))}

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="h5" component="h2" sx={{ fontFamily, fontWeight: 850, lineHeight: 1.25, letterSpacing: -0.2 }}>
                            Closing thoughts
                        </Typography>


                        <Typography component="p" sx={{ fontFamily, fontSize: "1.05rem", lineHeight: 1.85 }}>
                            The Rust rewrites were a market signal. They showed that when guarantees around safety, concurrency,
                            memory and startup matter enough, teams will rebuild from scratch to get them.
                            That signal triggered deep architectural change inside the JVM. Not incremental tuning, but
                            structural evolution shaped by modern workload demands.
                            What emerged is a platform transformed by pressure rather than displaced by it.
                        </Typography>

                        <Box
                            sx={{
                                borderLeft: 4,
                                borderColor: "grey.400",
                                pl: 2,
                                py: 0.5,
                                my: 1,
                            }}
                        >
                            <Typography
                                component="p"
                                sx={{
                                    fontFamily,
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    fontSize: "0.72rem",
                                    color: "text.secondary",
                                    mb: 0.5,
                                }}
                            >
                                The rewrite to Rust era wasn’t wrong, just early
                            </Typography>

                            <Typography
                                component="p"
                                sx={{
                                    fontFamily,
                                    fontStyle: "italic",
                                    color: "text.secondary",
                                    lineHeight: 1.9,
                                    fontSize: { xs: "1.02rem", sm: "1.1rem" },
                                }}
                            >
                                Hence, it forced Java to evolve.
                                As we move deeper into the last half of the 2020s, the question is no longer “Should we rewrite?”,
                                <br/>
                                It’s “Would we still rewrite, knowing what Java looks like now?”
                            </Typography>
                        </Box>
                        <Typography component="p" sx={{ fontFamily, fontSize: "1.05rem", lineHeight: 1.85 }}>
                            <i>Increasingly, the answer is no.</i>
                        </Typography>

                    </Stack>
                </Container>
            </Box>
        </>
    );
}
